import { useMemo, useRef, useState } from 'react';
import type { OrderRecord } from '@adiox/api';
import {
  ConfirmDialog,
  formatServerDateTime,
  useDisplayZone,
  useMoneyFormat,
  useSession,
  usePrefsValue,
} from '@adiox/ui';
import { ContextMenu, type MenuItem } from '../../chart/ChartOverlays';
import { useSymbols } from '../../market/useSymbols';
import { useStream } from '../../stream/stream';
import type { Position } from '../../stream/accountModel';
import { useQuoteSubscription } from '../../stream/useQuoteSubscription';
import { useWorkspace } from '../../workspace/workspaceStore';
import { bulkActions, oppositesOf, type BulkAction } from '../../trading/bulkActions';
import { EditDialog, type Editing } from '../../trading/TradePanel';
import type { Outcome } from '../../trading/tradeClient';
import { ClassicOrderWindow } from './ClassicOrderWindow';
import { useOrderWindow } from './orderWindowStore';
import {
  DEFAULT_SORT,
  nextSort,
  pendingDistance,
  positionTotals,
  sortPositions,
  type PositionSort,
  type PositionSortKey,
} from '../../trading/tradeMath';
import { useActiveLogin, useTradeClient } from '../../trading/useTrading';
import { marginTone } from '../accountTone';

const tone = (v: number | null) => (v === null ? '' : v > 0 ? 'text-up' : v < 0 ? 'text-down' : '');

const th = 'px-1.5 py-0.5 text-left font-normal';
const td = 'px-1.5 py-0.5 whitespace-nowrap';
/** The small "×" that closes a position or cancels an order in place (Modify is still reached by double-click or the
 * right-click menu; its own button is kept in the DOM for screen readers, just hidden, instead of removed). */
const rowClose =
  'grid h-4 w-4 place-items-center rounded leading-none text-muted hover:bg-down hover:text-black disabled:cursor-not-allowed disabled:opacity-30';

/** The shared columns of the merged Trade table: positions, the summary row and pending orders all use these. */
const COLUMNS: { key: PositionSortKey | 'actions'; label: string; right?: boolean; sortable?: boolean }[] = [
  { key: 'symbol', label: 'Symbol', sortable: true },
  { key: 'ticket', label: 'Ticket', sortable: true },
  { key: 'timeOpen', label: 'Time', sortable: true },
  { key: 'side', label: 'Type', sortable: true },
  { key: 'volume', label: 'Volume', right: true, sortable: true },
  { key: 'priceOpen', label: 'Price', right: true, sortable: true },
  { key: 'stopLoss', label: 'S/L', right: true, sortable: true },
  { key: 'takeProfit', label: 'T/P', right: true, sortable: true },
  { key: 'price', label: 'Price', right: true, sortable: true },
  { key: 'profit', label: 'Profit', right: true, sortable: true },
  { key: 'actions', label: '' },
];

const selectOrders = (orders: Record<number, OrderRecord>) =>
  Object.values(orders)
    .filter((o) => o.status === 'Placed')
    .sort((a, b) => a.ticket - b.ticket);

/**
 * The Trade tab as MT5 draws it: one table, sorted open positions, an inline account-summary row, then pending
 * orders — instead of a separate account line and two stacked tables. Same actions as the dockable Trade panel
 * (sort, double-click to modify, right-click menu, bulk close/cancel), condensed to the classic terminal's density.
 */
export function ClassicTradeTable() {
  const login = useActiveLogin();
  const client = useTradeClient();
  const account = useStream((s) => (login === undefined ? undefined : s.accounts[login]));
  const hubState = useStream((s) => s.hubState);
  const quotes = useStream((s) => s.quotes);
  const zone = useDisplayZone();
  const fmt = useMoneyFormat();
  const confirmClose = usePrefsValue((s) => s.data.confirmClose === true);
  const symbols = useSymbols();
  const select = useWorkspace((s) => s.select);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const running = useRef(false);
  const [sort, setSort] = useState<PositionSort>(DEFAULT_SORT);
  const [bulk, setBulk] = useState<BulkAction | null>(null);
  const [closing, setClosing] = useState<Position | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const orderWindow = useOrderWindow((s) => s.context);
  const openOrderWindow = useOrderWindow((s) => s.open);
  const closeOrderWindow = useOrderWindow((s) => s.close);
  const [menu, setMenu] = useState<{ x: number; y: number; title: string; items: MenuItem[] } | null>(null);
  const bulkButton = useRef<HTMLButtonElement>(null);

  const all = useMemo(() => Object.values(account?.positions ?? {}), [account?.positions]);
  const positions = useMemo(() => sortPositions(all, sort), [all, sort]);
  const totals = useMemo(() => positionTotals(all), [all]);
  const orders = useMemo(() => selectOrders(account?.orders ?? {}), [account?.orders]);
  useQuoteSubscription(orders.map((o) => o.symbol));
  const digitsOf = (symbol: string) => symbols.data?.find((s) => s.name === symbol)?.digits ?? 5;
  const currency = account?.currency ?? '';
  const online = hubState === 'connected';
  const readOnly = useSession().user?.readOnly === true;
  const canAct = online && !readOnly;

  const run = async <T,>(id: string, action: () => Promise<Outcome<T>>, done: (value: T) => string) => {
    if (running.current) return;
    running.current = true;
    setBusy(id);
    setNotice(null);
    const outcome = await action();
    running.current = false;
    setBusy(null);
    setNotice(
      outcome.ok ? { tone: 'ok', text: done(outcome.value) } : { tone: 'error', text: outcome.message },
    );
  };

  const closeNow = (p: Position) =>
    void run(
      `c${p.ticket}`,
      () => client!.closePosition(p.ticket),
      (r) => `Position #${p.ticket} closed at ${r.price.toFixed(digitsOf(p.symbol))}.`,
    );
  const requestClose = (p: Position) => (confirmClose ? setClosing(p) : closeNow(p));
  const cancelOrder = (o: OrderRecord) =>
    void run(
      `x${o.ticket}`,
      () => client!.cancelOrder(o.ticket),
      () => `Order #${o.ticket} cancelled.`,
    );

  const bulkRun = (b: BulkAction) => {
    const r = b.request;
    if (r.type === 'closeAll') {
      return void run(
        'bulk',
        () => client!.closeAll(r.body),
        (x) =>
          x.outcomes.length === 0
            ? 'Nothing matched any more: no position was closed.'
            : x.closed === x.outcomes.length
              ? `Closed ${x.closed} position${x.closed === 1 ? '' : 's'}.`
              : `Closed ${x.closed} of ${x.outcomes.length}; the rest could not be closed.`,
      );
    }
    if (r.type === 'closeByAll') {
      return void run(
        'bulk',
        () => client!.closeByAll(r.symbol),
        (x) =>
          x.outcomes.length === 0
            ? 'No buy and sell pairs left to close against each other.'
            : x.closed === x.outcomes.length
              ? `Closed ${x.closed} pair${x.closed === 1 ? '' : 's'} against each other.`
              : `Closed ${x.closed} of ${x.outcomes.length} pairs; the rest could not be closed.`,
      );
    }
    return void run(
      'bulk',
      () => client!.cancelAll(r.symbol),
      (x) =>
        x.outcomes.length === 0
          ? 'There were no pending orders left to delete.'
          : x.deleted === x.outcomes.length
            ? `Deleted ${x.deleted} pending order${x.deleted === 1 ? '' : 's'}.`
            : `Deleted ${x.deleted} of ${x.outcomes.length}; the rest could not be deleted.`,
    );
  };

  const asItems = (actions: BulkAction[]): MenuItem[] =>
    actions.map((a) => ({
      key: a.id,
      label: a.label,
      disabled: a.count === 0 || !canAct || busy !== null,
      danger: true,
      onSelect: () => setBulk(a),
    }));

  const closeByNow = (p: Position, other: Position) =>
    void run(
      `cb${p.ticket}`,
      () => client!.closeBy(p.ticket, other.ticket),
      () => `Closed #${p.ticket} against #${other.ticket}: no second spread paid.`,
    );

  const rowMenu = (p: Position, x: number, y: number) =>
    setMenu({
      x,
      y,
      title: `Position #${p.ticket} · ${p.symbol}`,
      items: [
        {
          label: 'Modify, trailing stop or close part…',
          onSelect: () => setEditing({ kind: 'position', row: p }),
        },
        {
          label: 'Close position',
          danger: true,
          disabled: !canAct || busy !== null,
          onSelect: () => requestClose(p),
        },
        ...oppositesOf(p, all)
          .slice(0, 4)
          .map((o) => ({
            key: `by${o.ticket}`,
            label: `Close by #${o.ticket} (${o.side.toLowerCase()} ${o.volume})`,
            disabled: !canAct || busy !== null,
            onSelect: () => closeByNow(p, o),
          })),
        { label: `Show ${p.symbol} on the chart`, onSelect: () => select(p.symbol) },
        ...asItems(bulkActions(all, orders, p.symbol)),
      ],
    });

  if (login === undefined) return <p className="p-2 text-xs text-muted">No trading account.</p>;
  if (!account?.loaded) return <p className="p-2 text-xs text-muted">Loading account…</p>;

  const f = account.figures;
  const marginLevelTone = marginTone(f, account);
  const colSpanBeforeProfit = COLUMNS.length - 2; // every column up to (not including) Profit and Actions

  return (
    <section className="flex h-full flex-col bg-panel" aria-label="Trade" data-stale={!online}>
      <div className={`min-h-0 flex-1 overflow-auto ${online ? '' : 'opacity-50'}`}>
        <table className="classic-table w-full text-xs" aria-label="Positions and pending orders">
          <thead className="sticky top-0">
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.key} scope="col" className={`${th} ${c.right ? 'text-right' : ''}`}>
                  {c.sortable ? (
                    <button
                      type="button"
                      className="hover:text-fg"
                      onClick={() => setSort(nextSort(sort, c.key as PositionSortKey))}
                    >
                      {c.key === 'profit' ? `Profit (${currency})` : c.label}
                      <span aria-hidden="true">
                        {sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </span>
                    </button>
                  ) : (
                    <span className="sr-only">Actions</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {positions.map((p) => {
              const d = digitsOf(p.symbol);
              return (
                <tr
                  key={`p${p.ticket}`}
                  data-testid="position-row"
                  data-ticket={p.ticket}
                  onClick={() => select(p.symbol)}
                  onDoubleClick={() => openOrderWindow({ kind: 'position', row: p })}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    rowMenu(p, e.clientX, e.clientY);
                  }}
                  title="Double-click S/L or T/P to modify; double-click elsewhere to open the order window"
                  className="cursor-default select-none hover:bg-panel2"
                >
                  <td className={`${td} font-semibold`}>{p.symbol}</td>
                  <td className={`${td} num`}>{p.ticket}</td>
                  <td className={`${td} num text-muted`}>
                    {formatServerDateTime(p.timeOpen, zone.offsetAt)}
                  </td>
                  <td className={`${td} ${p.side === 'Buy' ? 'text-up' : 'text-down'}`}>
                    {p.side.toLowerCase()}
                  </td>
                  <td className={`${td} num text-right`}>{p.volume}</td>
                  <td className={`${td} num text-right`}>{p.priceOpen.toFixed(d)}</td>
                  <td
                    className={`${td} num text-right`}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setEditing({ kind: 'position', row: p });
                    }}
                  >
                    {p.stopLoss > 0 ? p.stopLoss.toFixed(d) : '—'}
                  </td>
                  <td
                    className={`${td} num text-right`}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setEditing({ kind: 'position', row: p });
                    }}
                  >
                    {p.takeProfit > 0 ? p.takeProfit.toFixed(d) : '—'}
                  </td>
                  <td className={`${td} num text-right`}>{p.price === null ? '—' : p.price.toFixed(d)}</td>
                  <td
                    className={`${td} num text-right font-semibold ${tone(p.profit)}`}
                    data-testid="position-profit"
                  >
                    {p.profit === null ? '—' : fmt(p.profit)}
                  </td>
                  <td className={`${td} text-right whitespace-nowrap`}>
                    <button
                      type="button"
                      className="sr-only"
                      onClick={() => setEditing({ kind: 'position', row: p })}
                      aria-label={`Modify position ${p.ticket}`}
                    >
                      Modify
                    </button>
                    <button
                      type="button"
                      className={rowClose}
                      disabled={!canAct || busy !== null}
                      aria-label={`Close position ${p.ticket}`}
                      title="Close position"
                      onClick={() => requestClose(p)}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
            {orders.map((o) => {
              const d = digitsOf(o.symbol);
              const distance = pendingDistance(o, quotes[o.symbol], d);
              return (
                <tr
                  key={`o${o.ticket}`}
                  data-testid="order-row"
                  data-ticket={o.ticket}
                  onClick={() => select(o.symbol)}
                  onDoubleClick={() => openOrderWindow({ kind: 'order', row: o })}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setMenu({
                      x: e.clientX,
                      y: e.clientY,
                      title: `Order #${o.ticket} · ${o.symbol}`,
                      items: [
                        { label: 'Modify…', onSelect: () => setEditing({ kind: 'order', row: o }) },
                        {
                          label: 'Cancel order',
                          danger: true,
                          disabled: !canAct || busy !== null,
                          onSelect: () => cancelOrder(o),
                        },
                        { label: `Show ${o.symbol} on the chart`, onSelect: () => select(o.symbol) },
                      ],
                    });
                  }}
                  title={`Double-click S/L or T/P to modify; double-click elsewhere to open the order window · ${distance === null ? '' : `${distance} points away`}`}
                  className="cursor-default select-none hover:bg-panel2"
                >
                  <td className={`${td} font-semibold`}>{o.symbol}</td>
                  <td className={`${td} num`}>{o.ticket}</td>
                  <td className={`${td} num text-muted`}>
                    {formatServerDateTime(o.timeSetup, zone.offsetAt)}
                  </td>
                  <td className={td}>{o.type.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}</td>
                  <td className={`${td} num text-right`}>{o.volumeCurrent}</td>
                  <td className={`${td} num text-right`}>
                    {o.priceOrder.toFixed(d)}
                    {o.priceStopLimit > 0 ? ` / ${o.priceStopLimit.toFixed(d)}` : ''}
                  </td>
                  <td
                    className={`${td} num text-right`}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setEditing({ kind: 'order', row: o });
                    }}
                  >
                    {o.stopLoss > 0 ? o.stopLoss.toFixed(d) : '—'}
                  </td>
                  <td
                    className={`${td} num text-right`}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setEditing({ kind: 'order', row: o });
                    }}
                  >
                    {o.takeProfit > 0 ? o.takeProfit.toFixed(d) : '—'}
                  </td>
                  <td className={`${td} num text-right`} data-testid="order-market-price">
                    {quotes[o.symbol] ? quotes[o.symbol]!.bid.toFixed(d) : '—'}
                  </td>
                  <td className={`${td} text-right text-muted`}>placed</td>
                  <td className={`${td} text-right whitespace-nowrap`}>
                    <button
                      type="button"
                      className="sr-only"
                      onClick={() => setEditing({ kind: 'order', row: o })}
                      aria-label={`Modify order ${o.ticket}`}
                    >
                      Modify
                    </button>
                    <button
                      type="button"
                      className={rowClose}
                      disabled={!canAct || busy !== null}
                      aria-label={`Cancel order ${o.ticket}`}
                      title="Cancel order"
                      onClick={() => cancelOrder(o)}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* The account line stays at the bottom of the list while the trades scroll. */}
          <tfoot className="classic-account-foot sticky bottom-0 z-[1]">
            <tr className="classic-account-line font-semibold" data-testid="account-summary-row">
              <th scope="row" colSpan={colSpanBeforeProfit} className={`${td} text-left`}>
                <span aria-hidden="true">⊕ </span>
                Balance:{' '}
                <span className="num">
                  {fmt(f.balance)} {currency}
                </span>
                {f.credit !== 0 && (
                  <>
                    {'  '}Credit: <span className="num">{fmt(f.credit)}</span>
                  </>
                )}
                {'  '}Equity: <span className="num">{fmt(f.equity)}</span>
                {'  '}Margin: <span className="num">{fmt(f.margin)}</span>
                {'  '}Free Margin:{' '}
                <span className={`num ${f.freeMargin < 0 ? 'text-down' : ''}`}>{fmt(f.freeMargin)}</span>
                {'  '}Margin Level:{' '}
                <span
                  className={`num ${marginLevelTone === 'danger' ? 'text-down' : marginLevelTone === 'warn' ? 'text-warn' : ''}`}
                >
                  {f.margin === 0 ? '—' : `${fmt(f.marginLevel)} %`}
                </span>
              </th>
              <td className={`${td} num text-right ${tone(totals.profit)}`} data-testid="total-profit">
                {totals.profit === null ? '—' : fmt(totals.profit)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex items-center gap-3 border-t border-line px-2 py-1 text-xs">
        <span className="text-muted">
          {positions.length} position{positions.length === 1 ? '' : 's'} · {orders.length} order
          {orders.length === 1 ? '' : 's'}
        </span>
        <div className="min-w-0 flex-1" aria-live="polite">
          {notice && (
            <span
              role={notice.tone === 'error' ? 'alert' : 'status'}
              className={notice.tone === 'error' ? 'text-down' : 'sr-only'}
              data-testid="trade-notice"
            >
              {notice.text}
            </span>
          )}
        </div>
        {bulk ? (
          <>
            <span className="text-warn">{bulk.question}</span>
            <button
              type="button"
              className="rounded border border-line px-2 py-0.5 border-down text-down hover:border-accent"
              disabled={!canAct || busy !== null}
              onClick={() => {
                setBulk(null);
                bulkRun(bulk);
              }}
            >
              {bulk.confirmLabel}
            </button>
            <button
              type="button"
              className="rounded border border-line px-2 py-0.5 hover:border-accent"
              onClick={() => setBulk(null)}
            >
              Keep
            </button>
          </>
        ) : (
          <button
            ref={bulkButton}
            type="button"
            className="rounded border border-line px-2 py-0.5 hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
            aria-haspopup="menu"
            disabled={!canAct || (positions.length === 0 && orders.length === 0) || busy !== null}
            onClick={() => {
              const box = bulkButton.current!.getBoundingClientRect();
              const actions = bulkActions(all, orders);
              setMenu({
                x: box.left,
                y: box.top - 8 - 32 * (actions.length + 1),
                title: 'Close or delete',
                items: asItems(actions),
              });
            }}
          >
            Close or delete…
          </button>
        )}
      </div>

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          title={menu.title}
          items={menu.items}
          onClose={() => setMenu(null)}
        />
      )}
      <ConfirmDialog
        open={closing !== null}
        title={closing ? `Close position #${closing.ticket}?` : ''}
        confirmLabel="Close position"
        onCancel={() => setClosing(null)}
        onConfirm={() => {
          if (closing) closeNow(closing);
          setClosing(null);
        }}
      >
        {closing && (
          <p>
            {closing.side} {closing.volume} {closing.symbol} at the market price. Profit now:{' '}
            <span className={`num ${tone(closing.profit)}`}>
              {closing.profit === null ? '—' : `${fmt(closing.profit)} ${currency}`}
            </span>
            . You can turn this question off in Settings.
          </p>
        )}
      </ConfirmDialog>
      <EditDialog
        editing={editing}
        digits={editing ? digitsOf(editing.row.symbol) : 5}
        info={editing ? symbols.data?.find((s) => s.name === editing.row.symbol) : undefined}
        online={canAct}
        balance={f.balance}
        currency={currency}
        onClose={() => setEditing(null)}
        onResult={(tone, text) => setNotice({ tone, text })}
        client={client}
        showChart
      />
      <ClassicOrderWindow
        open={orderWindow !== null}
        context={orderWindow}
        allPositions={all}
        digitsOf={digitsOf}
        onClose={() => closeOrderWindow()}
        onCloseBy={(other) => {
          if (orderWindow?.kind === 'position') closeByNow(orderWindow.row, other);
          closeOrderWindow();
        }}
      />
    </section>
  );
}
